// Thin fetch wrapper for the Solution Cloudflare Worker API.
const API_URL = process.env.REACT_APP_API_URL || "";

const TOKEN_KEY = "solution.token";
const USER_KEY = "solution.user";

export const getToken = () => localStorage.getItem(TOKEN_KEY) || "";
export const setToken = (t) => (t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY));
export const getStoredUser = () => {
  try { return JSON.parse(localStorage.getItem(USER_KEY) || "null"); } catch { return null; }
};
export const setStoredUser = (u) => (u ? localStorage.setItem(USER_KEY, JSON.stringify(u)) : localStorage.removeItem(USER_KEY));

export class ApiError extends Error {
  constructor(message, status, payload) {
    super(message);
    this.status = status;
    this.payload = payload;
  }
}

async function request(path, { method = "GET", body, headers, auth = true, query } = {}) {
  const url = new URL(`${API_URL}${path}`);
  if (query) Object.entries(query).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") url.searchParams.set(k, v);
  });

  const h = new Headers(headers || {});
  if (body && !(body instanceof FormData)) h.set("Content-Type", "application/json");
  if (auth) {
    const t = getToken();
    if (t) h.set("Authorization", `Bearer ${t}`);
  }

  let res;
  try {
    let requestBody;
    if (!body) requestBody = undefined;
    else if (body instanceof FormData) requestBody = body;
    else requestBody = JSON.stringify(body);

    res = await fetch(url.toString(), {
      method,
      headers: h,
      body: requestBody,
    });
  } catch (e) {
    throw new ApiError("Network error — is the API reachable?", 0, { detail: String(e) });
  }

  const text = await res.text();
  let data;
  try { data = text ? JSON.parse(text) : {}; } catch { data = { raw: text }; }

  if (!res.ok) {
    if (res.status === 401 && auth) {
      // Session invalid — nuke and let AuthProvider bounce to /login
      setToken(null);
      setStoredUser(null);
    }
    throw new ApiError(data?.error || `${method} ${path} failed (${res.status})`, res.status, data);
  }
  return data;
}

export const api = {
  // Auth
  publicInstitute: (code) => request(`/api/public/institute`, { auth: false, query: { code } }),
  login: (email, password) => request(`/api/auth/login`, { method: "POST", body: { email, password }, auth: false }),
  otpRequest: (email) => request(`/api/auth/otp/request`, { method: "POST", body: { email }, auth: false }),
  otpVerify: (email, code) => request(`/api/auth/otp/verify`, { method: "POST", body: { email, code }, auth: false }),
  register: (payload) => request(`/api/auth/register`, { method: "POST", body: payload, auth: false }),
  changePassword: (current_password, new_password) =>
    request(`/api/auth/change-password`, { method: "POST", body: { current_password, new_password } }),
  logout: () => request(`/api/auth/logout`, { method: "POST" }).catch(() => ({})),
  me: () => request(`/api/me`),
  updateMe: (payload) => request(`/api/me`, { method: "PATCH", body: payload }),

  // Institute (self — admin can update policy)
  institute: (opts = {}) => request(`/api/institute`, { query: opts }),
  updateInstitute: (payload) => request(`/api/institute`, { method: "PATCH", body: payload }),

  // Categories (service catalogue)
  categories: (opts = {}) => request(`/api/categories`, { query: opts }),
  createCategory: (payload) => request(`/api/categories`, { method: "POST", body: payload }),
  updateCategory: (id, payload) => request(`/api/categories/${id}`, { method: "PATCH", body: payload }),

  // Tickets
  // SSO hand-off between Solution and the Events app
  ssoIssue: (target) => request(`/api/sso/issue`, { method: "POST", body: { target } }),
  ssoAccept: (token) => request(`/api/sso/accept`, { method: "POST", body: { token } }),
  tickets: (opts = {}) => request(`/api/tickets`, { query: opts }),
  ticket: (id) => request(`/api/tickets/${id}`),
  createTicket: (payload) => request(`/api/tickets`, { method: "POST", body: payload }),
  // Pre-submit similar-ticket check + subscribe (v8.1) — additive, existing
  // createTicket above is unchanged.
  checkSimilarTickets: (payload) => request(`/api/tickets/check-similar`, { method: "POST", body: payload }),
  watchTicket: (id) => request(`/api/tickets/${id}/watch`, { method: "POST", body: {} }),
  unwatchTicket: (id) => request(`/api/tickets/${id}/watch`, { method: "POST", body: { unwatch: true } }),
  knownIssues: () => request(`/api/problems/known-issues`),
  aiTriage: (incident) => request(`/api/ai/triage`, { method: "POST", body: { incident } }),
  aiSolve: (incident) => request(`/api/ai/solve`, { method: "POST", body: { incident } }),
  aiGuidance: (turns) => request(`/api/ai/guidance`, { method: "POST", body: { turns } }),
  aiTicketSummary: (id) => request(`/api/tickets/${id}/ai_summary`),
  aiTicketReply: (id) => request(`/api/tickets/${id}/ai_reply`, { method: "POST" }),
  kb: () => request(`/api/kb`),
  createKb: (payload) => request(`/api/kb`, { method: "POST", body: payload }),
  updateKb: (id, payload) => request(`/api/kb/${id}`, { method: "PATCH", body: payload }),
  deleteKb: (id) => request(`/api/kb/${id}`, { method: "DELETE" }),
  addComment: (id, body, is_internal = false) =>
    request(`/api/tickets/${id}/comments`, { method: "POST", body: { body, is_internal } }),
  ackTicket: (id) => request(`/api/tickets/${id}/acknowledge`, { method: "POST" }),
  setStatus: (id, status, note) => request(`/api/tickets/${id}/status`, { method: "POST", body: { status, note } }),
  assign: (id, assignee_id) => request(`/api/tickets/${id}/assign`, { method: "POST", body: { assignee_id } }),
  closeTicket: (id) => request(`/api/tickets/${id}/close`, { method: "POST" }),
  reopenTicket: (id, reason) => request(`/api/tickets/${id}/reopen`, { method: "POST", body: { reason } }),
  listAttachments: (id) => request(`/api/tickets/${id}/attachments`),
  uploadAttachment: async (id, file) => {
    const token = getToken();
    const res = await fetch(`${API_URL}/api/tickets/${id}/attachments`, {
      method: "POST",
      headers: {
        "Content-Type": file.type,
        "X-Filename": file.name,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: file,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new ApiError(data.error || "Upload failed", res.status, data);
    return data;
  },
  attachmentUrl: (attId) => `${API_URL}/api/attachments/${attId}`,
  report: (params) => request(`/api/reports`, { query: params }),
  // authorities (superadmin)
  // institute-level incharges + escalation (admin)
  authorities: (institute_id) => request(`/api/authorities`, { query: institute_id ? { institute_id } : {} }),
  createAuthority: (payload) => request(`/api/authorities`, { method: "POST", body: payload }),
  updateAuthority: (id, payload) => request(`/api/authorities/${id}`, { method: "PATCH", body: payload }),
  deleteAuthority: (id) => request(`/api/authorities/${id}`, { method: "DELETE" }),
  // sub-categories (central admin OR category head)
  deleteCategory: (id) => request(`/api/categories/${id}`, { method: "DELETE" }),
  subCategories: (params) => request(`/api/subcategories`, { query: params || {} }),
  createSubCategory: (payload) => request(`/api/subcategories`, { method: "POST", body: payload }),
  updateSubCategory: (id, payload) => request(`/api/subcategories/${id}`, { method: "PATCH", body: payload }),
  deleteSubCategory: (id) => request(`/api/subcategories/${id}`, { method: "DELETE" }),
  escalation: (catId, subId) => request(`/api/categories/${catId}/escalation`, { query: subId ? { sub_category_id: subId } : {} }),
  setEscalation: (catId, levels, subId) => request(`/api/categories/${catId}/escalation`, { method: "PUT", body: { levels, sub_category_id: subId || null } }),
  saAuthorities: (institute_id) => request(`/api/sa/authorities`, { query: { institute_id } }),
  saCreateAuthority: (payload) => request(`/api/sa/authorities`, { method: "POST", body: payload }),
  saUpdateAuthority: (id, payload) => request(`/api/sa/authorities/${id}`, { method: "PATCH", body: payload }),
  saDeleteAuthority: (id) => request(`/api/sa/authorities/${id}`, { method: "DELETE" }),
  saUpdateCategoryOwnership: (id, payload) => request(`/api/sa/categories/${id}`, { method: "PATCH", body: payload }),
  saEscalation: (catId) => request(`/api/sa/categories/${catId}/escalation`),
  saSetEscalation: (catId, payload) => request(`/api/sa/categories/${catId}/escalation`, { method: "PUT", body: payload }),

  // Users (also the global cross-institute browser for superadmin when no institute_id passed)
  users: (opts = {}) => request(`/api/users`, { query: opts }),
  createUser: (payload) => request(`/api/users`, { method: "POST", body: payload }),
  updateUser: (id, payload) => request(`/api/users/${id}`, { method: "PATCH", body: payload }),

  // Dashboard
  dashboard: (opts = {}) => request(`/api/dashboard`, { query: opts }),
  insights: (opts = {}) => request(`/api/dashboard/insights`, { query: opts }),

  // ── v6.5: CSAT ratings (public, token-based — no auth) ──
  rateInfo: (token) => request(`/api/public/rate`, { auth: false, query: { t: token } }),
  rateSubmit: (token, score, comment) =>
    request(`/api/public/rate`, { method: "POST", auth: false, query: { t: token }, body: { score, comment } }),

  // ── v6.5: canned responses / macros ──
  macros: (category_id) => request(`/api/macros`, { query: category_id ? { category_id } : {} }),
  createMacro: (payload) => request(`/api/macros`, { method: "POST", body: payload }),
  updateMacro: (id, payload) => request(`/api/macros/${id}`, { method: "PATCH", body: payload }),
  deleteMacro: (id) => request(`/api/macros/${id}`, { method: "DELETE" }),

  // ── v6.5: AI self-service deflection (try to solve before a ticket is raised) ──
  aiDeflect: (incident) => request(`/api/ai/deflect`, { method: "POST", body: { incident } }),
  aiDeflectFeedback: (deflection_id, outcome) =>
    request(`/api/ai/deflect/feedback`, { method: "POST", body: { deflection_id, outcome } }),

  // ── v6.6: business-hours SLA calendar ──
  businessCalendar: () => request(`/api/business-calendar`),
  saveBusinessCalendar: (payload) => request(`/api/business-calendar`, { method: "PUT", body: payload }),

  // ── v6.7: WhatsApp config (admin) ──
  whatsappConfig: () => request(`/api/whatsapp-config`),
  saveWhatsappConfig: (payload) => request(`/api/whatsapp-config`, { method: "PUT", body: payload }),

  // ── v6.8: assets ──
  assets: (opts = {}) => request(`/api/assets`, { query: opts }),
  createAsset: (payload) => request(`/api/assets`, { method: "POST", body: payload }),
  updateAsset: (id, payload) => request(`/api/assets/${id}`, { method: "PATCH", body: payload }),
  deleteAsset: (id) => request(`/api/assets/${id}`, { method: "DELETE" }),
  assetTickets: (id) => request(`/api/assets/${id}/tickets`),

  // ── v6.8: problems ──
  problems: (opts = {}) => request(`/api/problems`, { query: opts }),
  problem: (id) => request(`/api/problems/${id}`),
  createProblem: (payload) => request(`/api/problems`, { method: "POST", body: payload }),
  updateProblem: (id, payload) => request(`/api/problems/${id}`, { method: "PATCH", body: payload }),
  deleteProblem: (id) => request(`/api/problems/${id}`, { method: "DELETE" }),
  linkProblem: (id, ticket_id, unlink = false) => request(`/api/problems/${id}/link`, { method: "POST", body: { ticket_id, unlink } }),

  // ── v6.8: approvals ──
  requestApproval: (ticketId, payload) => request(`/api/tickets/${ticketId}/request-approval`, { method: "POST", body: payload }),
  ticketApprovals: (ticketId) => request(`/api/tickets/${ticketId}/approvals`),
  approvalInfo: (token) => request(`/api/public/approve`, { auth: false, query: { t: token } }),
  approvalDecide: (token, decision, note) => request(`/api/public/approve`, { method: "POST", auth: false, query: { t: token }, body: { decision, note } }),

  // ── ticket-action (email "Acknowledge / request reassignment" button) ──
  ticketActionInfo: (token) => request(`/api/public/ticket-action`, { auth: false, query: { t: token } }),
  ticketActionDo: (token, action, payload) =>
    request(`/api/public/ticket-action`, { method: "POST", auth: false, query: { t: token }, body: { action, ...payload } }),


  // ── v6.9: predictive SLA + leaderboard ──
  atRisk: () => request(`/api/sla/at-risk`),
  leaderboard: (days = 30) => request(`/api/leaderboard`, { query: { days } }),

  // ── v6.9: announcements / status page ──
  announcements: () => request(`/api/announcements`),
  publicAnnouncements: (institute) => request(`/api/public/announcements`, { auth: false, query: { institute } }),
  createAnnouncement: (payload) => request(`/api/announcements`, { method: "POST", body: payload }),
  updateAnnouncement: (id, payload) => request(`/api/announcements/${id}`, { method: "PATCH", body: payload }),
  deleteAnnouncement: (id) => request(`/api/announcements/${id}`, { method: "DELETE" }),

  // ── v7.0: API keys ──
  apiKeys: () => request(`/api/api-keys`),
  createApiKey: (payload) => request(`/api/api-keys`, { method: "POST", body: payload }),
  revokeApiKey: (id) => request(`/api/api-keys/${id}`, { method: "DELETE" }),

  // ── v7.0: webhooks ──
  webhooks: () => request(`/api/webhooks`),
  createWebhook: (payload) => request(`/api/webhooks`, { method: "POST", body: payload }),
  updateWebhook: (id, payload) => request(`/api/webhooks/${id}`, { method: "PATCH", body: payload }),
  deleteWebhook: (id) => request(`/api/webhooks/${id}`, { method: "DELETE" }),
  testWebhook: (id) => request(`/api/webhooks/${id}/test`, { method: "POST" }),
  webhookDeliveries: (id) => request(`/api/webhooks/${id}/deliveries`),

  // ── v7.0: white-label branding ──
  branding: () => request(`/api/branding`),
  saveBranding: (payload) => request(`/api/branding`, { method: "PUT", body: payload }),
  publicBranding: (institute) => request(`/api/public/branding`, { auth: false, query: { institute } }),

  // Superadmin — platform-wide email defaults
  saPlatform: () => request(`/api/sa/platform`),
  saUpdatePlatform: (payload) => request(`/api/sa/platform`, { method: "PATCH", body: payload }),

  // Superadmin — institutes
  saOverview: () => request(`/api/sa/overview`),
  saCharts: () => request(`/api/sa/charts`),
  saDeleteInstitute: (id, confirm_code) => request(`/api/sa/institutes/${id}`, { method: "DELETE", body: { confirm_code } }),
  saCreateInstitute: (payload) => request(`/api/sa/institutes`, { method: "POST", body: payload }),
  saUpdateInstitute: (id, payload) => request(`/api/sa/institutes/${id}`, { method: "PATCH", body: payload }),
  saRegenerateSetupCode: (id) => request(`/api/sa/institutes/${id}`, { method: "PATCH", body: { regenerate_setup_code: true } }),
  saInstituteUsers: (id) => request(`/api/sa/institutes/${id}/users`),
  saCreateInstituteUser: (id, payload) => request(`/api/sa/institutes/${id}/users`, { method: "POST", body: payload }),
  saTestEmail: (id, to) => request(`/api/sa/institutes/${id}/test-email`, { method: "POST", body: { to } }),
  saWhatsappConfig: (id) => request(`/api/sa/institutes/${id}/whatsapp`),
  saSaveWhatsappConfig: (id, payload) => request(`/api/sa/institutes/${id}/whatsapp`, { method: "PUT", body: payload }),
};

export default api;

// ── Bulk user import (named export used by BulkImportUsers.jsx) ──
export function bulkImportUsers({ users, role, send_welcome }) {
  return request("/api/users/bulk-import", {
    method: "POST",
    body: { users, role, send_welcome },
  });
}
